import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined'
import { ReactComponent as Logo } from '../../../assets/images/akive_logo.svg'
import { ROUTES } from '../../../constants/routes'
import useAuthSession from '../../../hooks/useAuthSession'
import './landing.css'

const asset = name => `${process.env.PUBLIC_URL}/landing/${name}`

const steps = [
  [
    '목표와 계획',
    '목표를 오늘의 계획으로',
    '아직 막연하고 큰 목표여도 괜찮아요. akive에서 목표를 계획으로, 계획을 오늘의 학습으로 하나씩 쪼개다 보면 오늘 해야 할 학습이 선명해져요.',
    'plans.jpg',
    '전체·주간 계획',
  ],
  [
    '실행',
    <>
      계획한 만큼,
      <br />
      한 걸음씩
    </>,
    '출석을 인증하고 학습을 시작해요. 지금 집중할 학습 항목을 고르고, 집중 시간과 완료량을 기록하세요.',
    'home.jpg',
    '출석 인증 · 학습 시간 기록',
  ],
  [
    '기록과 분석',
    '배운 내용도, 나의 흐름도 남겨요',
    '무엇을 배웠는지, 어떻게 시간을 보냈는지 기록해요. 남겨 둔 기록과 학습 리포트로 나의 현재 상태를 이해할 수 있어요.',
    'records.jpg',
    '하루 기록 · 학습 리포트',
  ],
  [
    '회고와 다음 계획',
    '오늘의 발견을 다음 계획으로',
    '잘한 점과 아쉬운 점을 돌아보고, 다음에 이어갈 행동을 직접 정해요. 회고에서 얻은 생각으로 내 계획을 다듬어 보세요.',
    'reflection.jpg',
    '회고 작성 · 계획 조정',
  ],
]

function Shot({ image, caption, eager = false }) {
  return (
    <figure className="lp-shot">
      <div className="lp-window" aria-hidden="true">
        <i />
        <i />
        <i />
        <span>akive · learning operating system</span>
      </div>
      <img
        src={asset(image)}
        alt={caption}
        width="1920"
        height="930"
        loading={eager ? 'eager' : 'lazy'}
        decoding="async"
      />
      <figcaption>{caption}</figcaption>
    </figure>
  )
}

export default function LandingPage() {
  const rootRef = useRef(null)
  const flowSectionRef = useRef(null)
  const coachingSectionRef = useRef(null)
  const [activePreviewIndex, setActivePreviewIndex] = useState(0)
  const isSignedIn = Boolean(useAuthSession()?.accessToken)
  const startPath = isSignedIn ? ROUTES.HOME : ROUTES.SIGN_UP
  const startLabel = isSignedIn ? '내 학습 이어가기' : '무료로 시작하기'

  useEffect(() => {
    const media = window.matchMedia('(prefers-reduced-motion: reduce)')
    let frame = 0

    const update = () => {
      frame = 0
      const rect = flowSectionRef.current.getBoundingClientRect()
      const progress = Math.min(
        1,
        Math.max(0, (window.innerHeight * 0.65 - rect.top) / rect.height),
      )

      rootRef.current.style.setProperty('--progress', media.matches ? 1 : progress)

      const marker = Math.min(160, window.innerHeight * 0.25)
      const currentSection = [
        ['#learning-flow', flowSectionRef.current],
        ['#coaching', coachingSectionRef.current],
      ].find(([, section]) => {
        const sectionRect = section.getBoundingClientRect()
        return sectionRect.top <= marker && sectionRect.bottom > marker
      })
      const nextHash = currentSection?.[0] || ''

      if (window.location.hash !== nextHash) {
        window.history.replaceState(
          window.history.state,
          '',
          `${window.location.pathname}${window.location.search}${nextHash}`,
        )
      }
    }

    const scroll = () => {
      if (!frame) frame = requestAnimationFrame(update)
    }

    const observer = typeof IntersectionObserver !== 'undefined'
      ? new IntersectionObserver(
        entries => entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible')
            observer.unobserve(entry.target)
          }
        }),
        { threshold: 0.12 },
      )
      : null

    if (observer && !media.matches) {
      rootRef.current.querySelectorAll('[data-reveal]').forEach((element) => {
        element.classList.add('reveal')
        observer.observe(element)
      })
    }

    update()
    window.addEventListener('scroll', scroll, { passive: true })
    window.addEventListener('resize', scroll)
    media.addEventListener?.('change', scroll)

    return () => {
      observer?.disconnect()
      cancelAnimationFrame(frame)
      window.removeEventListener('scroll', scroll)
      window.removeEventListener('resize', scroll)
      media.removeEventListener?.('change', scroll)
    }
  }, [])

  const cta = (
    <Link className="lp-cta" to={startPath}>
      {startLabel}
      <span aria-hidden="true">↗</span>
    </Link>
  )

  return (
    <div className="landing" ref={rootRef}>
      <a className="lp-skip" href="#landing-main">
        본문 바로가기
      </a>

      <header className="lp-header">
        <Link to={ROUTES.ABOUT} aria-label="akive 서비스 소개">
          <Logo />
        </Link>
        <nav aria-label="서비스 소개 메뉴">
          <a href="#learning-flow">학습 여정</a>
          <a href="#coaching">AI 코칭</a>
          <Link to={isSignedIn ? ROUTES.HOME : ROUTES.LOGIN}>
            {isSignedIn ? '홈으로' : '로그인'} ↗
          </Link>
        </nav>
      </header>

      <main id="landing-main">
        <section className="lp-hero">
          <div className="lp-hero-copy">
            <p className="lp-eyebrow">AKIVE — THE LEARNING OS</p>
            <h1>
              학습의 모든 순간을
              <br />
              <em>하나로</em>
            </h1>
            <p className="lp-lead">계획부터 실행, 기록, 회고까지</p>
            <p className="lp-sub">
              흩어진 학습의 모든 과정을
              <br className="lp-mobile" /> 하나의 흐름으로 연결하세요.
            </p>
            {cta}
          </div>
          <div className="lp-photo">
            <img
              src={asset('learner-hero.png')}
              alt="책상에서 노트북 화면을 보며 학습하는 여성"
              width="1672"
              height="941"
            />
          </div>
          <div className="lp-preview">
            <Shot
              image="home.jpg"
              caption="계획부터 회고까지, 한 화면에서 이어지는 학습"
              eager
            />
          </div>
        </section>

        <section className="lp-intro lp-wrap" data-reveal>
          <p className="lp-eyebrow">ONE CONNECTED SYSTEM</p>
          <h2>
            흩어져 있던 학습에
            <br />
            하나의 흐름이 생겨요
          </h2>
          <p>
            계획을 세운 곳에서 바로 시작하고,
            <br />
            남긴 기록을 돌아보며 다음 학습을 준비하세요.
          </p>
          <div className="lp-map">
            {['목표', '계획', '실행', '기록', '분석', '회고'].map((item, index) => (
              <span key={item}>
                <b>{item}</b>
                {index < 5 && <i aria-hidden="true">→</i>}
              </span>
            ))}
          </div>
          <small>↳ 다시, 더 나은 나의 계획으로</small>
        </section>

        <section
          id="learning-flow"
          className="lp-flow lp-wrap"
          ref={flowSectionRef}
          aria-label="akive의 학습 흐름"
        >
          <div className="lp-line" aria-hidden="true" />
          {steps.map(([name, title, text, image, tag], index) => (
            <article className="lp-step" key={name} data-reveal>
              <div className="lp-step-copy">
                <span className="lp-number">0{index + 1}</span>
                <p className="lp-eyebrow">{name}</p>
                <h2>{title}</h2>
                <p>{text}</p>
                <span className="lp-tag">{tag}</span>
              </div>
              <Shot image={image} caption={tag} />
            </article>
          ))}
        </section>

        <section className="lp-values">
          <div className="lp-wrap">
            <p className="lp-eyebrow">THE LEARNING OPERATING SYSTEM</p>
            <h2>학습을 운영하는 힘은 나에게</h2>
            <div className="lp-value-grid">
              {[
                [
                  '01 / 통합',
                  '여러 도구를 오가지 않아도',
                  '플래너, 타이머, 기록과 리포트. 학습에 필요한 활동을 한곳에 모았어요.',
                ],
                [
                  '02 / 연결',
                  '학습의 맥락이 이어지도록',
                  '계획과 실행 결과, 기록과 회고를 같은 학습 흐름 안에서 확인해요.',
                ],
                [
                  '03 / 자기주도성',
                  '내 학습의 방향은 내가',
                  '현재 상태를 이해하고, 무엇을 바꿀지 스스로 선택하세요.',
                ],
              ].map(([name, title, text]) => (
                <article key={name} data-reveal>
                  <span>{name}</span>
                  <h3>{title}</h3>
                  <p>{text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="lp-gallery lp-wrap" data-reveal>
          <p className="lp-eyebrow">A CLOSER LOOK</p>
          <h2>내 학습이 모이는 곳</h2>
          <p>소개용 학습 데이터로 촬영한 실제 akive 화면이에요.</p>
          <div className="lp-tabs" role="group" aria-label="서비스 화면 선택">
            {['홈', '학습 계획', '하루 기록', '회고 작성'].map((name, index) => (
              <button
                type="button"
                key={name}
                aria-pressed={activePreviewIndex === index}
                onClick={() => setActivePreviewIndex(index)}
              >
                {name}
              </button>
            ))}
          </div>
          <Shot
            image={['home.jpg', 'plans.jpg', 'records.jpg', 'reflection.jpg'][activePreviewIndex]}
            caption={[
              '홈 · 오늘의 학습 흐름',
              '학습 계획 · 계획량 관리',
              '하루 기록 · 학습 내용과 시간',
              '회고 작성 · 다음 학습 준비',
            ][activePreviewIndex]}
          />
        </section>

        <section id="coaching" className="lp-coaching" ref={coachingSectionRef}>
          <div className="lp-wrap lp-coaching-grid">
            <div data-reveal>
              <span className="lp-spark" aria-hidden="true">
                <AutoAwesomeOutlinedIcon />
              </span>
              <p className="lp-eyebrow">AI COACHING</p>
              <h2>
                혼자 고민하지 마세요
                <br />
                AI 코치가 함께할게요
              </h2>
              <p>
                학습 중 막히는 순간이 있다면,
                <br />
                궁금한 점과 고민을 편하게 이야기해 보세요.
              </p>
              <p>
                답변을 참고해 생각을 정리하고,
                <br />
                나에게 맞는 해결 방안을 선택해요.
              </p>
              <Link
                className="lp-text-link"
                to={isSignedIn ? ROUTES.HOME : ROUTES.LOGIN}
              >
                akive에서 시작하기 ↗
              </Link>
            </div>
            <Shot
              image="coaching.jpg"
              caption="AI 코칭 · 내가 질문하고 답변을 참고하는 대화"
            />
          </div>
        </section>

        <section className="lp-final lp-wrap" data-reveal>
          <p className="lp-eyebrow">START YOUR LEARNING OS</p>
          <h2>학습의 모든 순간을 하나로</h2>
          <p>이제, 나의 학습을 스스로 운영해 보세요.</p>
          {cta}
        </section>
      </main>

      <footer className="lp-footer lp-wrap">
        <Logo aria-label="akive" />
        <span>© {new Date().getFullYear()} akive</span>
        <a href="mailto:support@akive.net">support@akive.net</a>
        <Link to={ROUTES.TERMS_OF_SERVICE}>서비스 이용약관</Link>
        <Link to={ROUTES.PRIVACY_POLICY}>개인정보 처리방침</Link>
        <Link to={ROUTES.OPEN_SOURCE_LICENSES}>오픈소스 라이선스</Link>
      </footer>
    </div>
  )
}
